import { useRef, useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

import StreamingMessage from "../messages/StreamingMessage";
import { useAuth } from "../../../contexts/AuthContext"; // adjust path to wherever AuthContext lives

const NEAR_BOTTOM_PX = 150;

const STREAMING_UI_STYLES = `
  .thinking-shimmer {
    background-image: linear-gradient(
      90deg,
      rgb(var(--c-mutedText)) 0%,
      rgb(var(--c-mainText)) 50%,
      rgb(var(--c-mutedText)) 100%
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    animation: thinking-shimmer 2.4s linear infinite;
  }
  @keyframes thinking-shimmer {
    from { background-position: 200% center; }
    to   { background-position: -200% center; }
  }

  .thinking-pulse-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: rgb(var(--c-mainText));
    animation: thinking-pulse-dot 1.4s ease-in-out infinite;
  }
  @keyframes thinking-pulse-dot {
    0%, 100% { opacity: 0.3; transform: scale(0.85); }
    50%      { opacity: 1;   transform: scale(1.15); }
  }

  .streaming-content > *:last-child::after {
    content: "";
    display: inline-block;
    width: 2px;
    height: 1.05em;
    background: rgb(var(--c-mainText));
    margin-left: 3px;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: streaming-caret-blink 1s steps(2) infinite;
  }
  @keyframes streaming-caret-blink {
    to { visibility: hidden; }
  }

  /* Team-thread row spacing.
     Grouped (same author, < 120s) rows have pt-0.5 (~2px) for tight
     stacking. Non-grouped rows have pt-3 (~12px) — but pt-3 alone
     doesn't visually separate authors enough, so add an extra mt-2
     gap above any non-grouped row that isn't the first child. The
     :first-child guard prevents a phantom top margin on the very
     first message in the thread. */
  .team-thread-rows > .group:not(:first-child).pt-3 {
    margin-top: 0.5rem;
  }
`;

/**
 * Status line for the "user just sent, awaiting first token" window.
 *
 * Default text is "Thinking" while we're pre-status — guaranteed to
 * appear instantly the moment showLoading flips true, because this
 * component now renders OUTSIDE the thread's fade-in motion.div (see
 * structure note below). When a real status JSON lands and updates
 * `text`, AnimatePresence crossfades to the new label.
 *
 * `mode="popLayout"` and concurrent in/out (NOT mode="wait") avoid the
 * doubled-latency feel called out elsewhere in the codebase.
 */
const ThinkingStatus = ({ text }) => {
  const display = text || "Thinking";
  return (
    <div className="inline-flex items-center gap-2.5">
      <span className="thinking-pulse-dot" aria-hidden />
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={display}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="thinking-shimmer inline-block text-[14px] font-light tracking-wide whitespace-nowrap"
        >
          {display}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

const Skeleton = () => {
  const groups = [0, 1, 2, 3, 4, 5];
  return (
    <div className="space-y-8 animate-pulse">
      {groups.map((i) => {
        const altA = i % 2 === 0;
        return (
          <div key={i} className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-mutedText/20 shrink-0" />
              <div
                className={`h-3 ${altA ? "w-3/4" : "w-2/3"} max-w-[480px] rounded-md bg-mutedText/20`}
              />
            </div>
            <div className="flex gap-3">
              <div className="w-8 shrink-0" />
              <div className="flex-1 space-y-2.5 max-w-[720px]">
                <div className="h-3 w-full rounded-md bg-mutedText/20" />
                <div
                  className={`h-3 ${altA ? "w-11/12" : "w-5/6"} rounded-md bg-mutedText/20`}
                />
                <div
                  className={`h-3 ${altA ? "w-4/5" : "w-3/4"} rounded-md bg-mutedText/20`}
                />
                <div
                  className={`h-3 ${altA ? "w-2/3" : "w-1/2"} rounded-md bg-mutedText/20`}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Empty-state greeting.
 *
 * Two flavors:
 *   - Private/public (no `emptyStateMeta`): time-of-day + assistant prompt,
 *     personalized with the user's first name from AuthContext.
 *   - Team (`emptyStateMeta` provided): branched by conversation type
 *     (channel / dm / group). Same typography and motion as the
 *     private/public greeting so they read as a family.
 *
 * First-name source: pulled from `useAuth().user.first_name`. AuthContext
 * already fetches this on app mount for the login check, so we don't
 * need a separate request here. While the auth fetch is still in flight
 * `user` is null and we render "Good morning." — the comma+name swaps
 * in as soon as auth resolves.
 */
const Greeting = ({ emptyStateMeta }) => {
  const { user } = useAuth();
  const firstName = user?.first_name || "";

  let title;
  let subtitle;

  if (emptyStateMeta) {
    const { type, name } = emptyStateMeta;
    if (type === "channel") {
      const display = name?.startsWith("#") ? name : `#${name || "channel"}`;
      title = `Welcome to ${display}.`;
      subtitle = "This is the start of the channel. Say hi.";
    } else if (type === "dm") {
      title = name
        ? `This is your conversation with ${name}.`
        : "Start of your conversation.";
      subtitle = "Say hi.";
    } else {
      // group (and any unknown type — same treatment)
      title = name ? `Welcome to ${name}.` : "Welcome.";
      subtitle = "This is the start of the group. Say hi.";
    }
  } else {
    const hour = new Date().getHours();
    const period =
      hour < 5
        ? "evening"
        : hour < 12
          ? "morning"
          : hour < 18
            ? "afternoon"
            : "evening";
    title = firstName ? `Good ${period}, ${firstName}.` : `Good ${period}.`;
    subtitle = "What would you like to talk about?";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col items-center text-center select-none"
    >
      <h2 className="text-[26px] md:text-[30px] font-extralight text-mainText/90 tracking-tight leading-tight">
        {title}
      </h2>
      <p className="mt-2.5 text-[13.5px] text-mutedText font-light">
        {subtitle}
      </p>
    </motion.div>
  );
};

/**
 * ChatThread
 *
 * Loading-state model:
 *   `showLoading && messageCount === 0` → fetching thread history → skeleton.
 *   `showLoading && messageCount  >  0` → user just sent → shimmer status.
 *   `statusText`                        → control JSON arriving      → shimmer status.
 *   `streamingText`                     → text token streaming       → markdown render.
 *
 * Structure note:
 * Messages render inside a motion.div that fades+slides on
 * thread-switch. The streaming UI (Thinking + streaming text) renders
 * as a SIBLING of that motion.div, NOT inside it — otherwise the
 * thread-switch opacity animation masks the "Thinking" pill during
 * exactly the 350ms window between user send and first status JSON
 * arrival, which is the gap we want covered. Keeping it outside means
 * the pill is at full opacity the instant `showLoading` flips true.
 *
 * Spacing note:
 * Private/public use Q+A pair blocks that need consistent breathing
 * room between them — they get `space-y-5`. Team needs differentiated
 * spacing: tight when grouped (back-to-back same author), open when
 * not. The wrapper uses class `team-thread-rows`, and a stylesheet
 * rule (`team-thread-rows > .group:not(:first-child).pt-3`) adds a
 * top margin only to non-grouped rows after the first. TeamThreadView
 * already emits `pt-3` for non-grouped and `pt-0.5` for grouped, so
 * no changes there.
 */
const ChatThread = ({
  displayThisThread,
  streamingText,
  showLoading,
  showStreamingOutput,
  streamingCitations,
  statusText,
  messageCount,
  isTeamChat = false,
  threadKey = "thread",
  emptyStateMeta = null,
}) => {
  const scrollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevCountRef = useRef(messageCount);
  const [newMsgCount, setNewMsgCount] = useState(0);

  const scrollToBottom = useCallback((behavior = "instant") => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      isNearBottomRef.current = nearBottom;
      if (nearBottom && newMsgCount > 0) setNewMsgCount(0);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [newMsgCount]);

  useEffect(() => {
    if (!isTeamChat) {
      scrollToBottom();
      return;
    }
    const diff = messageCount - prevCountRef.current;
    prevCountRef.current = messageCount;
    if (diff <= 0) return;
    if (isNearBottomRef.current) scrollToBottom("smooth");
    else setNewMsgCount((p) => p + diff);
  }, [messageCount, isTeamChat, scrollToBottom]);

  useEffect(() => {
    if (!isTeamChat) scrollToBottom();
  }, [streamingText, statusText, isTeamChat, scrollToBottom]);

  useEffect(() => {
    if (!showLoading && !isTeamChat) scrollToBottom();
  }, [showLoading, isTeamChat, scrollToBottom]);

  const isLoadingThreadHistory = showLoading && messageCount === 0;

  const showThinking =
    (showLoading || !!statusText) &&
    !showStreamingOutput &&
    !isLoadingThreadHistory;

  // Empty state now applies to team too. Per spec, a team conversation
  // is always selected when chatType is team, so the only team empty
  // state is "selected conversation has zero messages".
  const isEmptyState =
    !showLoading && !showStreamingOutput && !showThinking && messageCount === 0;

  return (
    <div className="h-full min-h-0 flex flex-col relative">
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain pt-4 pb-48 [mask-image:linear-gradient(to_bottom,black_calc(100%-96px),transparent)] [-webkit-mask-image:linear-gradient(to_bottom,black_calc(100%-96px),transparent)]"
      >
        {isLoadingThreadHistory ? (
          <div className="w-full max-w-5xl px-6 flex flex-col justify-end min-h-full">
            <Skeleton />
          </div>
        ) : isEmptyState ? (
          <div className="w-full max-w-5xl px-6 flex flex-col justify-center min-h-full">
            {/* Slight upward translate so the greeting sits at visual
                center rather than geometric center (input is anchored
                at bottom). */}
            <div className="-translate-y-16 md:-translate-y-20">
              <Greeting emptyStateMeta={emptyStateMeta} />
            </div>
          </div>
        ) : (
          <div className="w-full max-w-5xl px-6 flex flex-col justify-end min-h-full">
            {/* Thread messages — fades/slides on thread switch only.
                Private/public: `space-y-5` between Q+A pair blocks.
                Team: `team-thread-rows` triggers the CSS rule above
                that selectively spaces non-grouped rows. */}
            <motion.div
              key={`thread-${threadKey}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className={`w-full ${isTeamChat ? "team-thread-rows" : "space-y-5"}`}
            >
              {displayThisThread}
            </motion.div>

            {/* Streaming UI — sibling of the messages wrapper, not a
                child. Renders at full opacity instantly the moment
                showLoading flips true. mt-1 keeps it visually attached
                to the previous message (matches the original -mt-4
                inside space-y-5 → ~4px). */}
            {(showThinking || showStreamingOutput) && (
              <div className="flex gap-3 mt-1">
                {/* Empty 8-wide gutter — content stays aligned with
                    assistant message content (which sits past the
                    message icon). No vertical line here; that read as
                    a colored stripe in some themes. */}
                <div className="w-8 shrink-0" />
                <div className="flex-1 min-w-0">
                  {showThinking && (
                    <div className="py-2">
                      <ThinkingStatus text={statusText} />
                    </div>
                  )}
                  {showStreamingOutput && (
                    <div className="streaming-content max-w-[720px] text-sm leading-relaxed text-mainText">
                      <StreamingMessage
                        text={streamingText}
                        citations={streamingCitations}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{STREAMING_UI_STYLES}</style>

      {newMsgCount > 0 && (
        <button
          onClick={() => {
            setNewMsgCount(0);
            scrollToBottom("smooth");
          }}
          className="absolute bottom-52 left-1/2 -translate-x-1/2 z-30 flex items-center gap-x-1.5 px-3 py-1.5 rounded-full bg-velBlue hover:bg-velBlue/80 text-white text-xs shadow-login-panel transition cursor-pointer"
        >
          <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.5} />
          {newMsgCount} new message{newMsgCount !== 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
};

export default ChatThread;
